import React, { useEffect, useState } from 'react'
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { getLeadsAnalytics, getLeadsCardsData, LeadsAnalyticsResponse, LeadsCardsResponse, downloadTotalBookingsData, downloadNewPatientsData, downloadShowedUpData, downloadGoogleReviewsClicked } from '../../../lib/apis/analytics';
import { getRouteSegment } from '../../../lib/utils/funcs';
import { format, startOfDay, startOfMonth } from 'date-fns';
import { Tilt } from 'react-tilt';
import CountUp from 'react-countup';
import { IoMdDownload } from 'react-icons/io';
import { MdOutlineFileDownload } from 'react-icons/md';
import { FiUsers } from 'react-icons/fi';
import NoDataState from '../../molecules/NoDataState';

type MetricKey =
  | "Total Bookings"
  | "New Patients"
  | "Google Reviews Clicked"
  | "Showed up (Check-in)";

const formatNumber = (value: string | number): number => {
  const num = typeof value === 'string' ? parseInt(value) : value;
  if (num >= 1000000) {
    return +(num / 1000000).toFixed(1);
  }
  if (num >= 10000) {
    return +(num / 1000).toFixed(1);
  }
  return num;
};

const getFormattedSuffix = (value: string | number): string => {
  const num = typeof value === 'string' ? parseInt(value) : value;
  if (num >= 1000000) {
    return 'M';
  }
  if (num >= 10000) {
    return 'K';
  }
  return '';
};

interface LeadCollectionGraphProps {
  hospitalId?: string;
  startDate?: string;
  endDate?: string;
  report?: boolean;
  funnelStats?: any;
  leadcards?: any;
}

export default function LeadCollectionGraph({ hospitalId, startDate, endDate, report, funnelStats, leadcards }: LeadCollectionGraphProps) {
  const [selectedMetrics, setSelectedMetrics] = React.useState<MetricKey[]>(["Total Bookings"]);
  const hospital_id = hospitalId || getRouteSegment(1);
  const [funnelData, setFunnelData] = useState<LeadsAnalyticsResponse>();
  const [cardsData, setCardsData] = useState<LeadsCardsResponse>();
  const [internalStartDate] = React.useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [internalEndDate] = React.useState(format(startOfDay(new Date()), 'yyyy-MM-dd'));
  const effectiveStartDate = startDate || internalStartDate;
  const effectiveEndDate = endDate || internalEndDate;

  const allMetricsData: Record<MetricKey, { name: string; value: number; valueRaw: number; percentageChange: number }> = {
    "Total Bookings": { name: "Total Appointments Confirmed", value: formatNumber(cardsData?.total_bookings || "0"), valueRaw: cardsData?.total_bookings ? parseInt(cardsData.total_bookings.toString()) : 0, percentageChange: cardsData?.pct_total_bookings || 0 },
    "New Patients": { name: "New Patients", value: formatNumber(cardsData?.new_patients || "0"), valueRaw: cardsData?.new_patients ? parseInt(cardsData.new_patients.toString()) : 0, percentageChange: cardsData?.pct_new_patients || 0 },
    "Google Reviews Clicked": { name: "Google Reviews Clicked", value: formatNumber(cardsData?.reviewed_bookings || "0"), valueRaw: cardsData?.reviewed_bookings ? parseInt(cardsData.reviewed_bookings.toString()) : 0, percentageChange: cardsData?.pct_reviewed_bookings || 0 },
    "Showed up (Check-in)": { name: "Showed up (Check-in)", value: formatNumber(cardsData?.showed_up || "0"), valueRaw: cardsData?.showed_up ? parseInt(cardsData.showed_up.toString()) : 0, percentageChange: cardsData?.pct_showed_up || 0 },
  };

  useEffect(() => {
    console.log(effectiveStartDate);

    const fetchLeadsData = async () => {
      if (!hospital_id) {
        console.error('Hospital ID is required');
        return;
      }
      try {
        const res = await getLeadsAnalytics({
          hospital_id: hospital_id,
          start_date: effectiveStartDate,
          end_date: effectiveEndDate,
        });
        setFunnelData(res);
      } catch (error) {
        console.error('Error fetching leads analytics:', error);
        setFunnelData(undefined);
      }
    }
    const fetchLeadsCardData = async () => {
      if (!hospital_id) {
        console.error('Hospital ID is required');
        return;
      }
      try {
        const res = await getLeadsCardsData({
          hospital_id: hospital_id,
          start_date: effectiveStartDate,
          end_date: effectiveEndDate,
        });
        setCardsData(res);
      } catch (error) {
        console.error('Error fetching leads cards data:', error);
        setCardsData(undefined);
      }
    }
    if (!funnelStats) {
      fetchLeadsData();
    }else{
      setFunnelData(funnelStats);
    }
    if (!leadcards) {
      fetchLeadsCardData();
    }else{
      setCardsData(leadcards)
    }

  }, [hospital_id, effectiveStartDate, effectiveEndDate])

  const funnelChartSeries = [
    {
      name: "Funnel",
      data: funnelData?.funnel ? [
        funnelData.funnel.started,
        funnelData.funnel.date_selected,
        funnelData.funnel.doctor_selected,
        funnelData.funnel.session_selected,
        funnelData.funnel.booking_confirmed
      ] : [],
    },
  ];

  // Dynamically calculate y-axis max
  const funnelMax = funnelChartSeries[0].data.length > 0 ? Math.ceil(Math.max(...funnelChartSeries[0].data) * 1.1) : undefined;

  const funnelChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '100%',
        distributed: true,
        isFunnel: true,
        borderRadius: 0,
      },
    },
    colors: [
      '#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0'
    ],
    dataLabels: {
      enabled: true,
      offsetX: 0,
      formatter: function (val, opts) {
        if (report) {
          // In report mode, include the category label with the value
          const categoryIndex = opts.dataPointIndex;
          const categoryLabels = [
            'Initial Contact',
            'Date selected',
            'Doctor Selected',
            'Session Selected',
            'Appt. Confirmed',
          ];
          const categoryLabel = categoryLabels[categoryIndex] || '';
          return `${categoryLabel}: ${val}`;
        }
        return val.toString();
      },
      dropShadow: {
        enabled: false
      },
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        colors: ['#fff']
      },
    },
    stroke: {
      width: 1,
      colors: ['#fff']
    },
    xaxis: {
      categories: [
        'Initial Contact',
        'Date selected ',
        'Doctor Selected',
        'Session Selected',
        'Appt. Confirmed',
      ],
    },
    yaxis: {
      max: funnelMax,
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: "horizontal",
        shadeIntensity: 0.5,
        gradientToColors: ['#ABE5FA', '#00E396', '#FEB019', '#FF4560', '#775DD0'],
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100]
      },
    },
    legend: {
      show: false,
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return val + " people"
        }
      }
    },
    title: {
      text: '',
      align: 'center',
    }
  };

  const handleMetricChange = (input: React.ChangeEvent<HTMLInputElement> | { target: { value: string; checked: boolean } }) => {
    const { value, checked } = 'target' in input ? input.target : { value: '', checked: false };
    setSelectedMetrics((prevMetrics) => {
      const newMetrics = checked
        ? [...prevMetrics, value as MetricKey]
        : prevMetrics.filter((metric) => metric !== value);
      if (newMetrics.length === 0) {
        return ["Total Bookings"];
      }
      return newMetrics;
    });
  };

  // Download handlers for each metric
  const handleDownload = async (metric: MetricKey) => {
    if (!hospital_id) return;
    const params = {
      hospital_id,
      start_date: effectiveStartDate,
      end_date: effectiveEndDate,
    };
    try {
      if (metric === "Total Bookings") {
        await downloadTotalBookingsData(params);
      } else if (metric === "New Patients") {
        await downloadNewPatientsData(params);
      } else if (metric === "Google Reviews Clicked") {
        await downloadGoogleReviewsClicked(params);
      } else if (metric === "Showed up (Check-in)") {
        await downloadShowedUpData(params);
      }
    } catch (e) {
      // Optionally show error to user
    }
  };

  return (
    <div className={`flex flex-col gap-6 mt-6 ${report ? "" : "lg:flex-row"}`}>
      <div className={`w-full ${report ? "w-full" : "lg:w-1/2"} grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white`}>
        {Object.values(allMetricsData).map((metric) => (
          <Tilt
            key={metric.name}
            className="Tilt"
            options={{
              max: 25,
              scale: 1.05,
              speed: 2000,
              glare: true,
              'max-glare': 1,
            }}
          >
            <div
              className={`p-4 rounded-lg cursor-pointer flex flex-col justify-between items-start h-full transition-all duration-300 hover:shadow-xl ${metric.name === "Total Bookings"
                  ? "bg-gradient-to-br from-blue-400 to-blue-600 text-white hover:from-blue-500 hover:to-blue-700"
                  : metric.name === "New Patients"
                    ? "bg-gradient-to-br from-cyan-400 to-teal-600 text-white hover:from-cyan-500 hover:to-teal-700"
                    : metric.name === "Google Reviews Clicked"
                      ? "bg-gradient-to-br from-orange-400 to-red-600 text-white hover:from-orange-500 hover:to-red-700"
                      : "bg-gradient-to-br from-purple-400 to-purple-600 text-white hover:from-purple-500 hover:to-purple-700"
                } ${report ? "pb-12" : ""}`}
              onClick={() =>
                handleMetricChange({
                  target: {
                    value: metric.name,
                    checked: !selectedMetrics.includes(metric.name as MetricKey),
                  },
                })
              }
            >
              <div className="w-full">
                <div className="flex justify-between items-start w-full mb-2">
                  <h3 className="text-lg font-medium text-white pr-2">{metric.name}</h3>

                  {/* Enhanced Download Button */}
                  <div className="relative group">
                    <button
                      onClick={e => { e.stopPropagation(); handleDownload(metric.name as MetricKey); }}
                      className="relative w-8 h-8 rounded-lg flex items-center justify-center border border-white/10 bg-white/5 backdrop-blur-[2px] hover:backdrop-blur-lg hover:bg-white/20 hover:border-white/20 hover:scale-110 hover:-translate-y-1 transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-white/50 shadow-lg hover:shadow-white/20"
                      aria-label={`Download ${metric.name} data`}
                      type="button"
                    >
                      <MdOutlineFileDownload className="w-5 h-5 text-white/90 transition-transform duration-300 group-hover:translate-y-0.5" />

                      {/* Ripple Effect */}
                      <div className="absolute inset-0 rounded-lg bg-white/10 opacity-0 group-active:opacity-100 group-active:animate-ping transition-opacity"></div>
                    </button>

                    {/* Tooltip */}
                    <div className="absolute -top-12 right-0 bg-gray-900/90 backdrop-blur-sm text-white text-xs px-3 py-2 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transform group-hover:translate-y-0 translate-y-2 transition-all duration-300 pointer-events-none z-10">
                      Download CSV
                      <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900/90"></div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-1 mt-1">
                  {metric.percentageChange > 0 ? (
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 10l7-7m0 0l7 7m-7-7v18"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      ></path>
                    </svg>
                  )}
                  <span className="text-lg font-semibold text-white">
                    {Math.abs(metric.percentageChange)}%
                  </span>
                </div>
              </div>

              <span className="text-6xl font-bold text-white">
                <CountUp
                  end={formatNumber(metric.valueRaw)}
                  duration={1.5}
                  decimals={getFormattedSuffix(metric.valueRaw) ? 1 : 0}
                  separator={getFormattedSuffix(metric.valueRaw) ? '' : ','}
                  suffix={getFormattedSuffix(metric.valueRaw)}
                />
              </span>
            </div>
          </Tilt>
        ))}
      </div>
      <div className={`w-full ${report ? "w-full" : "lg:w-1/2"} bg-white p-2 px-4 rounded-lg shadow`}>
        {funnelChartSeries[0].data.length > 0 && funnelChartSeries[0].data.some(value => value > 0) ? (
          <ReactApexChart options={funnelChartOptions} series={funnelChartSeries} type="bar" height={350} />
        ) : (
          <NoDataState
            title="No Lead Data Available"
            description="No lead funnel data found for the selected date range. Try adjusting your date range."
            icon={<FiUsers className="w-16 h-16 text-blue-400" />}
            className="py-8"
            iconContainerClassName="bg-gradient-to-br from-blue-50 to-blue-100 rounded-full p-6 mb-4"
          />
        )}
      </div>
    </div>
  );
}

